import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  error?: string;
  leftIcon?: React.ReactNode;
  showPasswordToggle?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, leftIcon, showPasswordToggle, className = '', type, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const enableToggle = isPassword && (showPasswordToggle ?? true);
    const inputType = enableToggle && showPassword ? 'text' : type;

    return (
      <div className="w-full flex flex-col items-start group">
        {label && (
          <label className="mb-2 block text-[10px] font-label font-bold uppercase text-on-surface-variant tracking-[0.2em] pl-1 opacity-60">
            {label}
          </label>
        )}
        <div className="relative w-full flex items-center">
          {leftIcon && <div className="absolute left-6 pointer-events-none">{leftIcon}</div>}
          <input
            type={inputType}
            className={`flex h-14 w-full rounded-2xl border-none bg-surface-container-low py-4 text-base text-on-surface font-body outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-300 ${
              leftIcon ? 'pl-14' : 'pl-6'
            } ${enableToggle ? 'pr-14' : 'pr-6'} ${
              error
                ? 'bg-error-container/10 text-error placeholder:text-error/30 ring-2 ring-error/20'
                : 'placeholder:text-on-surface-variant/30 hover:bg-surface-container-high/60'
            } ${className}`}
            ref={ref}
            {...props}
          />
          {enableToggle && (
            <button
              type="button"
              onClick={() => setShowPassword(prev => !prev)}
              aria-label={showPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
              className="absolute right-5 flex items-center justify-center text-on-surface-variant/50 hover:text-on-surface transition-colors p-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          )}
        </div>
        {error && (
          <p className="mt-2 text-[10px] font-label font-bold uppercase tracking-widest text-error pl-1">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
